import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { type FC, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import type { UserStatus } from '@furality/fox-client/models';
import { assertLoggedIn, ReportError, useAuth, useQuery } from '@furality/frontend-stack';
import { Button, Slice } from '@furality/furality-purpose';
import type { RegistrationLevel } from '@furality/registration-client/models';
import fox from '#/api/fox.ts';
import useTimedState from '#/hooks/useTimedState.ts';
import useAgeVerification from '#/vite/hooks/useAgeVerification.ts';
import ErrorCard from '@/components/_status/ErrorCard.tsx';
import LoadingCard from '@/components/_status/LoadingCard.tsx';
import SuccessCard from '@/components/_status/SuccessCard.tsx';
import UnknownCard from '@/components/_status/UnknownCard.tsx';
import { FullLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import PageDisabled from '@/components/PageDisabled.tsx';
import regLevelI18nKey from '@/lib/helpers/regLevelI18nKey.ts';
import useFlag from '@/lib/hooks/useFlag.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';
import useRegistrationQuery from '@/lib/queries/useRegistrationQuery.ts';

const OfflineInVRChat = () => (
  <ErrorCard>
    <Trans
      i18nKey="status.offline_in_vrchat"
      components={{
        h: <h3 className="my-0" />,
        p: <div className="lead" />,
      }}
    />
  </ErrorCard>
);

const DoNotDisturb = () => (
  <ErrorCard>
    <Trans
      i18nKey="status.dnd_in_vrchat"
      components={{
        h: <h3 className="my-0" />,
        p: <p className="lead" />,
        Button: (
          <Button
            variant="outline-white"
            className="col-md-auto col-12"
            rounded
            as="a"
            href="https://furality.org/new-player-guide#the-social-menu"
            target="_blank"
            rel="noreferrer"
          />
        ),
      }}
    />
  </ErrorCard>
);

const OnlineInVRChat = () => (
  <SuccessCard>
    <Trans i18nKey="status.online_in_vrchat" components={{ h: <h3 className="my-0" /> }} />
  </SuccessCard>
);

type OnlineStatusProps = {
  status: UserStatus;
};

const OnlineStatus: FC<OnlineStatusProps> = ({ status }) => {
  switch (status) {
    case 'offline':
      return <OfflineInVRChat />;

    case 'busy':
      return <DoNotDisturb />;
    default:
      return <OnlineInVRChat />;
  }
};

const NotConventionGroupMember = () => (
  <ErrorCard>
    <Trans
      i18nKey="status.not_convention_group_member"
      components={{
        h: <h3 className="my-0" />,
        p: <p className="lead" />,
        Button: (
          <Button
            variant="outline-white"
            className="col-md-auto col-12"
            rounded
            as="a"
            href="https://vrc.group/FURLTY.7057"
            target="_blank"
            rel="noreferrer"
          />
        ),
      }}
    />
  </ErrorCard>
);

const ConventionGroupMember = () => (
  <SuccessCard>
    <Trans i18nKey="status.convention_group_member" components={{ h: <h3 className="my-0" /> }} />
  </SuccessCard>
);

const ConventionGroupMemberUnknown = () => (
  <UnknownCard>
    <Trans
      i18nKey="status.convention_group_member.unknown"
      components={{ h: <h4 className="my-0" /> }}
    />
  </UnknownCard>
);

type ConventionGroupMemberStatusProps = {
  status?: boolean;
};

const ConventionGroupMemberStatus: FC<ConventionGroupMemberStatusProps> = ({ status }) =>
  typeof status === 'undefined' ? (
    <ConventionGroupMemberUnknown />
  ) : status ? (
    <ConventionGroupMember />
  ) : (
    <NotConventionGroupMember />
  );

type SummaryProps = {
  allGood?: boolean;
  isConventionGroupMember?: boolean;
  goToProfile: () => void;
  refetch: () => void;
  waiting: boolean;
  pauseStatusRetry: boolean;
};
const Summary: FC<SummaryProps> = ({
  allGood,
  isConventionGroupMember,
  goToProfile,
  waiting,
  refetch,
  pauseStatusRetry,
}) => {
  if (typeof allGood === 'undefined' || typeof isConventionGroupMember === 'undefined') {
    return (
      <UnknownCard>
        <Trans
          i18nKey="status.status_unknown"
          components={{
            h: <h3 className="my-0" />,
            p: <p />,
            TryAgainButton: (
              <Button
                variant="outline-danger"
                className="col-md-auto col-12"
                rounded
                onClick={refetch}
                disabled={pauseStatusRetry}
              />
            ),
            ProfileButton: (
              <Button
                variant="outline-white"
                className="col-md-auto col-12"
                rounded
                onClick={goToProfile}
              />
            ),
          }}
        />
      </UnknownCard>
    );
  }
  if (allGood && isConventionGroupMember) {
    return (
      <SuccessCard>
        <Trans
          i18nKey="status.all_set"
          components={{
            h: <h3 className="my-0" />,
            p: <p />,
            Button: (
              <Button
                variant="outline-white"
                className="col-md-auto col-12"
                rounded
                onClick={goToProfile}
              />
            ),
          }}
        />
      </SuccessCard>
    );
  }
  if (waiting) {
    return (
      <LoadingCard>
        <Trans
          i18nKey="status.please_wait"
          components={{
            h: <h3 className="my-0" />,
            p: <div />,
          }}
        />
      </LoadingCard>
    );
  }
  return (
    <ErrorCard>
      <Trans
        ns="status"
        i18nKey="issues_detected"
        components={{
          h: <h3 className="mt-2" />,
          p: <p className="lead" />,
          Button: (
            <Button
              variant="outline-white"
              className="col-md-auto col-12"
              rounded
              onClick={refetch}
              disabled={waiting}
            />
          ),
        }}
      />
    </ErrorCard>
  );
};

// biome-ignore lint/style/useComponentExportOnlyModules: tanstack route
const Status = () => {
  // TODO: shouldn't need profile
  const { t } = useTranslation(['common', 'status', 'profile']);
  const navigate = useNavigate();

  const auth = useAuth();
  assertLoggedIn(auth);
  const reg = useRegistrationQuery();
  const { isOver18, verificationStatus } = useAgeVerification();

  const registered = reg.data?.status === 'confirmed';
  const registrationLevel: RegistrationLevel | undefined = reg.data?.registrationLevel;

  const featuresReady = useFlagsStatus().flagsReady;
  const enableStatusPage = useFlag('status');

  const [awaitingSocketResponse, setAwaitingSocketResponse] = useState(false);

  const [pauseStatusRetry, setPauseStatusRetry] = useTimedState(false, 10000);

  const status = useQuery({
    queryKey: [QUERY_KEYS.Status],
    queryFn: async () => {
      const response = await fox.value.userGetStatus();
      switch (response.status) {
        case 200:
          if (response.data.friendRequest === 'pending') {
            setAwaitingSocketResponse(true);
          }
          if (
            typeof response.data.allGood === 'undefined' ||
            typeof response.data.isConventionGroupMember === 'undefined'
          ) {
            setPauseStatusRetry(true);
          }
          return response.data;
        default:
          throw new ReportError(response);
      }
    },
  });

  if (!enableStatusPage) {
    return <PageDisabled />;
  }

  if (!featuresReady) {
    return <PageLoading />;
  }

  if (status.isError) {
    return <Alert variant="danger">{t('status.errors.failed_to_get_status')}</Alert>;
  }

  return (
    <Slice>
      <div className="mb-5 text-center">
        <Trans
          i18nKey="status.intro"
          components={{
            h: <h1 className="text-center" />,
            p: <p className="mb-0 d-none d-md-block text-center" />,
          }}
        />
      </div>
      <Row>
        <Col xl={6}>
          <SuccessCard>
            <Trans
              i18nKey="status.account_connected"
              components={{
                h: <h3 className="my-0" />,
                p: <div className="lead" />,
              }}
              values={{ userDisplayName: auth.user.name }}
            />
          </SuccessCard>
        </Col>
        <Col xl={6}>
          {registered ? (
            <SuccessCard>
              <Trans
                i18nKey="status.registered"
                components={{
                  h: <h3 className="my-0" />,
                  p: <div className="lead" />,
                }}
                values={{
                  registrationLevel: t(regLevelI18nKey(registrationLevel)),
                }}
              />
            </SuccessCard>
          ) : (
            <ErrorCard>
              You are not registered! You shouldn&apos;t even be able to see this page :(
            </ErrorCard>
          )}
        </Col>

        <Col xl={6}>
          {status.isSuccess ? (
            <OnlineStatus status={status.data.status} />
          ) : (
            <LoadingCard>{t('status.loading_vrchat_status')}</LoadingCard>
          )}
        </Col>

        <Col xl={6}>
          {status.isSuccess ? (
            <ConventionGroupMemberStatus status={status.data.isConventionGroupMember} />
          ) : (
            <LoadingCard>{t('status.loading_convention_group_member_status')}</LoadingCard>
          )}
        </Col>
        <Col xl={6}>
          {isOver18 ? (
            <SuccessCard>
              <Trans
                ns="status"
                i18nKey="age_gated_instances_joinable"
                components={{
                  h: <h3 className="my-0" />,
                  p: <div className="lead" />,
                }}
              />
            </SuccessCard>
          ) : (
            <ErrorCard>
              <Trans
                ns="status"
                i18nKey="age_gated_instances_not_joinable"
                components={{
                  h: <h3 className="my-0" />,
                  p: <div className="lead" />,
                  Button: (
                    <Button
                      variant="outline-white"
                      className="col-md-auto col-12 mt-2"
                      rounded
                      onClick={() => verificationStatus.refetch()}
                    />
                  ),
                  AgeVerificationButton: (
                    <Link
                      to="/age-verification"
                      className="btn btn-white rounded col-md-auto col-12 mt-2"
                    />
                  ),
                }}
              />
            </ErrorCard>
          )}
        </Col>
      </Row>
      {status.isSuccess && (
        <>
          <hr />
          <Row className="mt-5">
            <Col>
              <Summary
                waiting={awaitingSocketResponse}
                allGood={status.data.allGood}
                isConventionGroupMember={status.data.isConventionGroupMember}
                goToProfile={() => void navigate({ to: '/profile' })}
                refetch={() => status.refetch()}
                pauseStatusRetry={pauseStatusRetry}
              />
            </Col>
          </Row>
        </>
      )}
    </Slice>
  );
};

export const Route = createFileRoute('/status')({
  component: () => (
    <FullLayout title="shared.navigation.status">
      <Status />
    </FullLayout>
  ),
});
